/**
 * Iter-225L — Operations Summary Tile
 *
 * Command-center scoreboard for admins. Each chip is clickable and
 * deep-links to the relevant queue. Pulls counts from
 * /api/admin/operations/summary.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Activity, Loader2, PlayCircle, ListChecks, CheckCircle2, Lock,
  Archive, DollarSign, FileText, Users, MessageSquareText,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// Order + presentation matches the user's spec exactly so the tile reads
// top-to-bottom like the lifecycle of an event.
// Iter-225L-fix — every `to` MUST resolve to a real route in App.js.
// Audited 2026-05-25 against the live route table:
//   /admin/shifts, /admin/operations, /admin/invoices, /admin/payouts,
//   /admin/payment-flow are real; /admin/timesheets and /admin/recaps
//   are NOT — those chips were 404'ing.
const TILES = [
  { key: 'active',                label: 'Active',                Icon: Activity,         cls: 'text-emerald-700 bg-emerald-50 border-emerald-200',  to: '/admin/shifts?status=open' },
  { key: 'in_progress',           label: 'In Progress',           Icon: PlayCircle,       cls: 'text-cyan-700    bg-cyan-50    border-cyan-200',     to: '/admin/shifts?status=in_progress' },
  { key: 'ready_for_closeout',    label: 'Ready for Closeout',    Icon: ListChecks,       cls: 'text-amber-700   bg-amber-50   border-amber-200',    to: '/admin/operations?filter=ready_for_closeout' },
  { key: 'completed',             label: 'Completed',             Icon: CheckCircle2,     cls: 'text-indigo-700  bg-indigo-50  border-indigo-200',   to: '/admin/shifts?status=completed' },
  { key: 'closed',                label: 'Closed',                Icon: Lock,             cls: 'text-slate-700   bg-slate-50   border-slate-200',    to: '/admin/shifts?status=closed' },
  { key: 'archived',              label: 'Archived',              Icon: Archive,          cls: 'text-zinc-700    bg-zinc-50    border-zinc-200',     to: '/admin/shifts?status=archived' },
  { key: 'payroll_pending',       label: 'Payroll Pending',       Icon: DollarSign,       cls: 'text-rose-700    bg-rose-50    border-rose-200',     to: '/admin/staff-payments?tab=ready_for_payment' },
  { key: 'invoice_pending',       label: 'Invoice Pending',       Icon: FileText,         cls: 'text-orange-700  bg-orange-50  border-orange-200',   to: '/admin/invoices?status=pending' },
  // Sprint T2P — repointed to the unified lifecycle tabs so the count
  // on the tile matches the rows on the page (single SQL predicate).
  { key: 'staff_payment_pending', label: 'Staff Payment Pending', Icon: Users,            cls: 'text-fuchsia-700 bg-fuchsia-50 border-fuchsia-200',  to: '/admin/staff-payments?tab=ready_for_payment' },
  { key: 'recap_pending',         label: 'Recap Pending',         Icon: MessageSquareText,cls: 'text-purple-700  bg-purple-50  border-purple-200',   to: '/admin/operations/drilldown/recap-pending' },
  { key: 'clock_out_missing',     label: 'Clock-Out Missing',     Icon: PlayCircle,       cls: 'text-rose-700    bg-rose-50    border-rose-200',     to: '/admin/operations/drilldown/clock-out-missing' },
  { key: 'ready_for_payment',     label: 'Ready for Payment',     Icon: DollarSign,       cls: 'text-emerald-700 bg-emerald-50 border-emerald-200',  to: '/admin/staff-payments?tab=ready_for_payment' },
];

export default function OperationsSummaryTile() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await fetch(`${API}/api/admin/operations/summary`, { headers: headers() });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const body = await res.json();
        if (active) setData(body);
      } catch (e) {
        if (active) setError(e.message || 'Failed to load');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  return (
    <Card className="bg-white border-gray-200 shadow-sm" data-testid="operations-summary-tile">
      <CardHeader className="py-3 px-4 flex flex-row items-center justify-between">
        <CardTitle className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <Activity className="w-4 h-4 text-cyan-600" />
          Operations Summary
        </CardTitle>
        {loading && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
      </CardHeader>
      <CardContent className="px-4 pb-4">
        {error ? (
          <p className="text-xs text-red-600" data-testid="ops-summary-error">{error}</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
            {TILES.map(({ key, label, Icon, cls, to }) => {
              const count = data?.[key];
              const isLoading = loading && count === undefined;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => navigate(to)}
                  data-testid={`ops-tile-${key}`}
                  className={`text-left rounded-lg border px-3 py-2.5 transition-all hover:shadow-sm hover:-translate-y-0.5 active:translate-y-0 ${cls}`}
                >
                  <div className="flex items-center justify-between">
                    <Icon className="w-4 h-4 opacity-80" />
                    <span className="text-lg font-bold tabular-nums" data-testid={`ops-count-${key}`}>
                      {isLoading ? '…' : (count ?? 0)}
                    </span>
                  </div>
                  <p className="mt-1 text-[11px] font-medium uppercase tracking-wide opacity-90">
                    {label}
                  </p>
                </button>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
